import { fmtMonthDayYear } from '@concord/domain';
import { Button, Panel, PanelHead, useToast } from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../../lib/api';
import { caseKey } from './CasePage';

interface Removed {
  kind: 'record' | 'guest';
  id: string;
  label: string;
  removedAt: string;
}

/** Admin: nothing is ever deleted; removed records and guests are listed here and can come back. */
export function RemovedItems({ caseRef, tz, readOnly }: { caseRef: string; tz: string; readOnly: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['removed', caseRef],
    queryFn: () => api<{ items: Removed[] }>(`/staff/cases/${caseRef}/removed`),
  });
  const restore = useMutation({
    mutationFn: (r: Removed) =>
      api(`/staff/cases/${caseRef}/${r.kind === 'record' ? 'records' : 'guests'}/${r.id}/restore`, {
        body: {},
      }),
    onSuccess: async (_d, r) => {
      await qc.invalidateQueries({ queryKey: ['removed', caseRef] });
      await qc.invalidateQueries({ queryKey: caseKey(caseRef) });
      toast(`${r.label} restored.`);
    },
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
  const items = q.data?.items ?? [];
  if (!items.length) return null;
  return (
    <Panel aria-label="Removed items">
      <PanelHead title="Removed items" sub="Admin · kept for recovery" />
      <div className="pbody" style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
        {items.map((r) => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
            <span>
              {r.kind === 'record' ? 'Record' : 'Guest'}: {r.label}
              <span className="sub2"> · removed {fmtMonthDayYear(new Date(r.removedAt), tz)}</span>
            </span>
            {!readOnly && (
              <Button
                variant="sec"
                style={{ marginLeft: 'auto' }}
                busy={restore.isPending && restore.variables?.id === r.id}
                onClick={() => restore.mutate(r)}
              >
                Restore
              </Button>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}
